import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import DataTable from "../../common/dataTable";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import HoverPreview from "../../common/HoverPreview/HoverPreview";
import Sidemodal from "../../common/sidemodal/sidemodal";
import Dropdown from "../../common/dropDown";
import { useCapaAccess } from "./capaAccess";
import {
  fetchCapaTasks,
  updateCapaActionItemStatus,
  updateCapaApproverRecordStatus,
} from "./capaService";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import utils from "../../../utils/utils";
import { getAvatarColor, getInitials, stripHtml } from "../../../utils/avatarUtils";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import "./CapaPages.scss";

const TASK_TYPE_FILTER_OPTIONS = [
  { value: "corrective", label: "Corrective" },
  { value: "preventive", label: "Preventive" },
  { value: "implementation", label: "Implementation" },
  { value: "approval", label: "Approval" },
  { value: "verification", label: "Verification" },
];
const DUE_SCHEDULE_FILTER_OPTIONS = [
  { value: "Overdue", label: "Overdue" },
  { value: "Upcoming", label: "Upcoming" },
];
const TASK_WORKFLOW_STATUS_OPTIONS = [
  "Accepted",
  "Rejected",
  "Not Started",
  "In Progress",
  "Partially Completed",
  "Completed",
  "Deferred",
  "Effective",
  "Not Effective",
];

function getTaskTypeBadgeStyle(typeRaw) {
  const type = String(typeRaw || "").trim().toLowerCase();
  if (type.includes("corrective")) return { backgroundColor: "#fee2e2", color: "#991b1b" };
  if (type.includes("preventive")) return { backgroundColor: "#dcfce7", color: "#166534" };
  if (type.includes("action planning"))
    return { backgroundColor: "#dbeafe", color: "#1e40af" };
  if (type.includes("approval")) return { backgroundColor: "#fef3c7", color: "#92400e" };
  if (type.includes("implementation"))
    return { backgroundColor: "#cffafe", color: "#155e75" };
  if (type.includes("verification"))
    return { backgroundColor: "#e2e8f0", color: "#334155" };
  return { backgroundColor: "#f1f5f9", color: "#334155" };
}

function getTaskStatusBadgeStyle(statusRaw) {
  const status = String(statusRaw || "").trim().toLowerCase();
  if (status.includes("effective")) return { backgroundColor: "#dcfce7", color: "#166534" };
  if (status.includes("not effective")) return { backgroundColor: "#fee2e2", color: "#991b1b" };
  if (status.includes("partially")) return { backgroundColor: "#fef3c7", color: "#92400e" };
  if (status.includes("completed")) return { backgroundColor: "#dcfce7", color: "#166534" };
  if (status.includes("in progress")) return { backgroundColor: "#dbeafe", color: "#1d4ed8" };
  if (status.includes("not started")) return { backgroundColor: "#e2e8f0", color: "#334155" };
  if (status.includes("deferred")) return { backgroundColor: "#ede9fe", color: "#5b21b6" };
  if (status.includes("rejected") || status.includes("reject"))
    return { backgroundColor: "#fee2e2", color: "#991b1b" };
  if (status.includes("accepted") || status.includes("approver"))
    return { backgroundColor: "#dcfce7", color: "#166534" };
  return { backgroundColor: "#f1f5f9", color: "#334155" };
}

function getDueDateBadgeStyle(dueDateRaw) {
  const d = utils.parseDateInput(dueDateRaw);
  if (!d) return { backgroundColor: "#f1f5f9", color: "#334155", text: "—" };
  const now = Date.now();
  const isOverdue = d.getTime() < now;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return {
    backgroundColor: isOverdue ? "#fee2e2" : "#dbeafe",
    color: isOverdue ? "#991b1b" : "#1d4ed8",
    text: `${day}/${month}/${year}`,
  };
}

function normalizeTaskTypeForFilter(typeRaw) {
  const type = String(typeRaw || "").trim().toLowerCase();
  if (type.includes("corrective")) return "corrective";
  if (type.includes("preventive")) return "preventive";
  if (type.includes("implementation")) return "implementation";
  if (type.includes("approval")) return "approval";
  if (type.includes("verification")) return "verification";
  return "";
}

const CapaTasks = () => {
  const navigate = useNavigate();
  const { userData, canCapaMyWork } = useCapaAccess();
  const [rows, setRows] = useState([]);
  const [allStaffOptions, setAllStaffOptions] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [taskDetailOpen, setTaskDetailOpen] = useState(null);
  const [actingTaskKey, setActingTaskKey] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    types: [],
    statuses: [],
    dueSchedules: [],
    assignees: [],
  });
  const [draftFilters, setDraftFilters] = useState({
    types: [],
    statuses: [],
    dueSchedules: [],
    assignees: [],
  });
  const canAccessTasks = canCapaMyWork;

  const loadTasks = useCallback(async () => {
    const t = await fetchCapaTasks(userData);
    setRows(t);
  }, [userData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const t = await fetchCapaTasks(userData);
      if (!cancelled) setRows(t);
    })();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userData?.OrganizationId || !userData?.StaffId) return;
      try {
        const response = await api.post(API_ENDPOINTS.USER.GET_USERLIST, {
          OrganizationId: userData.OrganizationId,
          StaffId: userData.StaffId,
          Action: "Get",
          AccessCode: "",
          Password: "",
          HaveProfileViewRights: 1,
          HaveProfileEditRights: 0,
          HaveDelegationLogViewRights: 1,
          HaveDelegationLogEditRights: 0,
          HavingLineManagerRights: 1,
          HaveStudyTaskArchiveRights: 0,
          CreatedBy: userData.StaffId,
        });
        if (cancelled || response?.success !== 1) return;
        const list = Array.isArray(response?.data?.UsersActivity)
          ? response.data.UsersActivity
          : [];
        const mapped = list
          .map((u) => String(u?.Name || u?.EmailId || "").trim())
          .filter(Boolean)
          .map((name) => ({ value: name, label: name }));
        setAllStaffOptions(mapped);
      } catch {
        if (!cancelled) setAllStaffOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  const statusOptions = TASK_WORKFLOW_STATUS_OPTIONS;
  const assigneeOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...allStaffOptions.map((o) => String(o.value || "").trim()),
          ...rows.map((r) => String(r.OwnerName || "").trim()),
        ]),
      )
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [allStaffOptions, rows],
  );

  const activeFilterCount = useMemo(
    () =>
      [
        Array.isArray(appliedFilters.types) && appliedFilters.types.length > 0,
        Array.isArray(appliedFilters.statuses) && appliedFilters.statuses.length > 0,
        Array.isArray(appliedFilters.dueSchedules) && appliedFilters.dueSchedules.length > 0,
        Array.isArray(appliedFilters.assignees) && appliedFilters.assignees.length > 0,
      ].filter(Boolean)
        .length,
    [appliedFilters],
  );

  const openFilterPanel = useCallback(() => {
    setDraftFilters(appliedFilters);
    setFilterOpen(true);
  }, [appliedFilters]);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(draftFilters);
    setFilterOpen(false);
  }, [draftFilters]);

  const handleClearFilters = useCallback(() => {
    const empty = { types: [], statuses: [], dueSchedules: [], assignees: [] };
    setDraftFilters(empty);
    setAppliedFilters(empty);
  }, []);

  const filteredRows = useMemo(
    () =>
      rows.filter((r) => {
        const typeFilterValue = normalizeTaskTypeForFilter(r.Type);
        const status = String(r.TaskStatus || "").trim();
        const assignee = String(r.OwnerName || "").trim();
        const due = utils.parseDateInput(r.DueDate);
        const now = Date.now();
        const dueSchedule =
          due == null ? "" : due.getTime() < now ? "Overdue" : "Upcoming";
        if (
          Array.isArray(appliedFilters.types) &&
          appliedFilters.types.length > 0 &&
          !appliedFilters.types.includes(typeFilterValue)
        ) {
          return false;
        }
        if (
          Array.isArray(appliedFilters.statuses) &&
          appliedFilters.statuses.length > 0 &&
          !appliedFilters.statuses.includes(status)
        ) {
          return false;
        }
        if (
          Array.isArray(appliedFilters.dueSchedules) &&
          appliedFilters.dueSchedules.length > 0 &&
          !appliedFilters.dueSchedules.includes(dueSchedule)
        ) {
          return false;
        }
        if (
          Array.isArray(appliedFilters.assignees) &&
          appliedFilters.assignees.length > 0 &&
          !appliedFilters.assignees.includes(assignee)
        ) {
          return false;
        }
        return true;
      }),
    [rows, appliedFilters],
  );

  const columns = [
    { label: "CAPA No.", accessor: "CapaNo" },
    {
      label: "Task",
      accessor: "Title",
      render: (row) => {
        const raw = String(row.Title || "");
        const plain = stripHtml(raw);
        if (!plain.trim()) return <span className="text-muted">—</span>;
        return (
          <HoverPreview
            content={raw}
            plainTextForPreview={plain}
            isHtml={/<[^>]+>/.test(raw)}
            anchorShowHtml={false}
            previewLength={99999}
            appendEllipsis={false}
            maxWidth={480}
            maxHeight={260}
          />
        );
      },
    },
    {
      label: "Due Date",
      accessor: "DueDate",
      render: (row) => {
        const due = getDueDateBadgeStyle(row.DueDate);
        return (
          <span
            className="badge rounded-pill"
            style={{
              backgroundColor: due.backgroundColor,
              color: due.color,
              fontSize: "0.68rem",
              fontWeight: 600,
              lineHeight: 1.1,
              padding: "0.2em 0.55em",
            }}
          >
            {due.text}
          </span>
        );
      },
    },
    {
      label: "Type",
      accessor: "Type",
      render: (row) => {
        const label = String(row.Type || "—").trim() || "—";
        return (
          <span
            className="badge rounded-pill"
            style={{
              ...getTaskTypeBadgeStyle(label),
              fontSize: "0.68rem",
              fontWeight: 600,
              lineHeight: 1.1,
              padding: "0.2em 0.55em",
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      label: "Status",
      accessor: "TaskStatus",
      render: (row) => {
        const label = String(row.TaskStatus || "—").trim() || "—";
        return (
          <span
            className="badge rounded-pill"
            style={{
              ...getTaskStatusBadgeStyle(label),
              fontSize: "0.68rem",
              fontWeight: 600,
              lineHeight: 1.1,
              padding: "0.2em 0.55em",
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      label: "Asignee",
      accessor: "OwnerName",
      align: "center",
      render: (row) => {
        const fullName = String(row.OwnerName || "").trim() || "—";
        return (
          <div className="capa-list-owner-only">
            <div
              className="capa-list-owner-avatar"
              style={{ backgroundColor: getAvatarColor(fullName) }}
              title={fullName}
            >
              {getInitials(fullName)}
            </div>
          </div>
        );
      },
    },
  ];

  const actions = [
    {
      label: "View details",
      className: "btn-outline-primary btn-sm",
      onClick: (row) => setTaskDetailOpen(row),
    },
    {
      label: "Open CAPA",
      className: "btn-primary btn-sm",
      onClick: (row) => {
        if (!row?.CapaId) return;
        navigate(`/capa/${row.CapaId}`);
      },
    },
  ];

  const canQuickDecide = useMemo(() => {
    if (!taskDetailOpen) return false;
    const cat = String(taskDetailOpen.TaskCategory || "").trim();
    const st = String(taskDetailOpen.TaskStatus || "").trim().toLowerCase();
    const pending = st === "" || st === "pending" || st === "n/a" || st === "na";
    return pending && (cat === "Action Planning" || cat === "Approval Request");
  }, [taskDetailOpen]);

  const handleQuickDecision = useCallback(
    async (nextStatus) => {
      if (!taskDetailOpen?.CapaId || !taskDetailOpen?.TaskRefId) return;
      const authRes = await openAuthModal({ showDescription: false });
      if (authRes?.Success !== 1) return;
      setActingTaskKey(String(taskDetailOpen.TaskKey || ""));
      try {
        if (taskDetailOpen.TaskRefType === "action") {
          await updateCapaActionItemStatus(
            taskDetailOpen.CapaId,
            taskDetailOpen.TaskRefId,
            nextStatus,
            "",
            userData,
          );
        } else if (taskDetailOpen.TaskRefType === "approval") {
          await updateCapaApproverRecordStatus(
            taskDetailOpen.CapaId,
            taskDetailOpen.TaskRefId,
            nextStatus,
            "",
            userData,
          );
        } else {
          return;
        }
        await loadTasks();
        setTaskDetailOpen((prev) =>
          prev && prev.TaskKey === taskDetailOpen.TaskKey
            ? { ...prev, TaskStatus: nextStatus }
            : prev,
        );
      } finally {
        setActingTaskKey("");
      }
    },
    [taskDetailOpen, userData, loadTasks],
  );

  if (!userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }
  if (!canAccessTasks) return <Navigate to="/notification-center" replace />;

  return (
    <div className="capa-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "My CAPA tasks" },
        ]}
        toolbarExtras={
          <div className="d-inline-block position-relative">
            <button
              type="button"
              className="actions-trigger capa-list-filter-trigger"
              onClick={openFilterPanel}
            >
              <i className="fas fa-filter me-1" aria-hidden />
              Filter
            </button>
            {activeFilterCount > 0 ? (
              <span
                role="button"
                tabIndex={0}
                className="capa-list-filter-trigger__badge position-absolute top-0 end-0"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClearFilters();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClearFilters();
                  }
                }}
                title="Clear filters"
                aria-label="Clear filters"
                style={{
                  zIndex: 10,
                  cursor: "pointer",
                  pointerEvents: "auto",
                  top: "-0.35rem",
                  right: "-0.35rem",
                }}
              >
                {activeFilterCount} ×
              </span>
            ) : null}
          </div>
        }
        actions={[
          { icon: "fas fa-sync-alt", label: "Refresh Data", onClick: loadTasks },
        ]}
      />
      <Sidemodal isOpen={filterOpen} onClose={() => setFilterOpen(false)} sidewidth="24rem">
        <div className="capa-list-filter-panel d-flex flex-column flex-grow-1 overflow-hidden">
          <div className="capa-list-filter-panel__header">
            <h2 id="capa-task-filter-title" className="h6 mb-0">
              Filter CAPA tasks
            </h2>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleApplyFilters();
            }}
          >
            <div className="capa-list-filter-panel__body">
              <section className="mb-3">
                <h3 className="small text-uppercase text-muted mb-2">Type</h3>
                <div className="d-flex flex-column gap-1">
                  {TASK_TYPE_FILTER_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className="d-flex align-items-center gap-2 small mb-0"
                    >
                      <input
                        type="checkbox"
                        checked={(draftFilters.types || []).includes(opt.value)}
                        onChange={() =>
                          setDraftFilters((f) => {
                            const current = Array.isArray(f.types) ? f.types : [];
                            const next = current.includes(opt.value)
                              ? current.filter((v) => v !== opt.value)
                              : [...current, opt.value];
                            return { ...f, types: next };
                          })
                        }
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </section>
              <section className="mb-3">
                <h3 className="small text-uppercase text-muted mb-2">Status</h3>
                <div className="d-flex flex-column gap-1">
                  {statusOptions.map((opt) => (
                    <label
                      key={opt}
                      className="d-flex align-items-center gap-2 small mb-0"
                    >
                      <input
                        type="checkbox"
                        checked={(draftFilters.statuses || []).includes(opt)}
                        onChange={() =>
                          setDraftFilters((f) => {
                            const current = Array.isArray(f.statuses) ? f.statuses : [];
                            const next = current.includes(opt)
                              ? current.filter((v) => v !== opt)
                              : [...current, opt];
                            return { ...f, statuses: next };
                          })
                        }
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </section>
              <section className="mb-3">
                <h3 className="small text-uppercase text-muted mb-2">Due schedule</h3>
                <div className="d-flex flex-column gap-1">
                  {DUE_SCHEDULE_FILTER_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className="d-flex align-items-center gap-2 small mb-0"
                    >
                      <input
                        type="checkbox"
                        checked={(draftFilters.dueSchedules || []).includes(opt.value)}
                        onChange={() =>
                          setDraftFilters((f) => {
                            const current = Array.isArray(f.dueSchedules)
                              ? f.dueSchedules
                              : [];
                            const next = current.includes(opt.value)
                              ? current.filter((v) => v !== opt.value)
                              : [...current, opt.value];
                            return { ...f, dueSchedules: next };
                          })
                        }
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </section>
              <section className="mb-3">
                <h3 className="small text-uppercase text-muted mb-2">Asignee</h3>
                <Dropdown
                  isMulti
                  options={assigneeOptions}
                  selected={assigneeOptions.filter((opt) =>
                    (draftFilters.assignees || []).includes(opt.value),
                  )}
                  onSelect={(items) =>
                    setDraftFilters((f) => ({
                      ...f,
                      assignees: Array.isArray(items)
                        ? items.map((x) => String(x.value))
                        : [],
                    }))
                  }
                />
              </section>
            </div>
            <div className="capa-list-filter-panel__footer">
              <div className="ms-auto d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleClearFilters}
                >
                  Clear filters
                </button>
                <button type="submit" className="btn btn-primary btn-sm">
                  Apply filters
                </button>
              </div>
            </div>
          </form>
        </div>
      </Sidemodal>
      {taskDetailOpen ? (
        <Sidemodal isOpen={Boolean(taskDetailOpen)} onClose={() => setTaskDetailOpen(null)} sidewidth="30rem">
          <div className="p-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <h5 className="mb-0 text-secondary-emphasis">
                {String(taskDetailOpen.CapaNo || "—").trim() || "—"}
              </h5>
              <span
                className="badge rounded-pill"
                style={{
                  ...getTaskStatusBadgeStyle(taskDetailOpen.TaskStatus),
                  fontSize: "0.68rem",
                  fontWeight: 600,
                }}
              >
                {String(taskDetailOpen.TaskStatus || "—").trim() || "—"}
              </span>
            </div>

            <div className="row g-3 small mb-3">
              <div className="col-12 col-md-4">
                <div className="capa-detail-summary__activity-field-caption mb-1">
                  TASK Type
                </div>
                <span
                  className="badge rounded-pill"
                  style={{
                    ...getTaskTypeBadgeStyle(taskDetailOpen.Type),
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    lineHeight: 1.1,
                    padding: "0.2em 0.55em",
                  }}
                >
                  {String(taskDetailOpen.Type || "—").trim() || "—"}
                </span>
              </div>
              <div className="col-12 col-md-4">
                <div className="capa-detail-summary__activity-field-caption mb-1">DUE DATE</div>
                {(() => {
                  const due = getDueDateBadgeStyle(taskDetailOpen.DueDate);
                  return (
                    <span
                      className="badge rounded-pill"
                      style={{
                        backgroundColor: due.backgroundColor,
                        color: due.color,
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        lineHeight: 1.1,
                        padding: "0.2em 0.55em",
                      }}
                    >
                      {due.text}
                    </span>
                  );
                })()}
              </div>
              <div className="col-12 col-md-4">
                <div className="capa-detail-summary__activity-field-caption mb-1">ASIGNEE</div>
                <div className="d-flex align-items-center gap-2 min-w-0">
                  <div
                    className="capa-list-owner-avatar flex-shrink-0"
                    style={{
                      backgroundColor: getAvatarColor(String(taskDetailOpen.OwnerName || "").trim() || "—"),
                    }}
                    title={String(taskDetailOpen.OwnerName || "").trim() || "—"}
                  >
                    {getInitials(String(taskDetailOpen.OwnerName || "").trim() || "—")}
                  </div>
                  <span className="text-truncate">
                    {String(taskDetailOpen.OwnerName || "").trim() || "—"}
                  </span>
                </div>
              </div>
            </div>

            <div className="capa-detail-summary__activity-field-caption mb-1">Task Details</div>
            <div className="capa-verification-detail-modal__rich-scroll mb-3">
              {/<[^>]+>/.test(String(taskDetailOpen.DetailsHtml || taskDetailOpen.Title || "")) ? (
                <div className="ql-snow">
                  <div
                    className="ql-editor"
                    dangerouslySetInnerHTML={{
                      __html: String(taskDetailOpen.DetailsHtml || taskDetailOpen.Title || ""),
                    }}
                  />
                </div>
              ) : (
                <div>{String(taskDetailOpen.DetailsPlain || taskDetailOpen.Title || "").trim() || "—"}</div>
              )}
            </div>

            <div className="capa-detail-summary__activity-field-caption mb-1">Verifier Comments</div>
            <div className="capa-verification-detail-modal__rich-scroll">
              {String(taskDetailOpen.VerifierComments || "").trim() || "—"}
            </div>

            <div className="row g-3 small mt-1">
              <div className="col-12 col-md-6">
                <div className="capa-detail-summary__activity-field-caption mb-1">
                  CREATED BY
                </div>
                <div className="d-flex align-items-center gap-2 min-w-0">
                  <div
                    className="capa-list-owner-avatar flex-shrink-0"
                    style={{
                      backgroundColor: getAvatarColor(
                        String(taskDetailOpen.CreatedByName || "").trim() || "—",
                      ),
                    }}
                    title={String(taskDetailOpen.CreatedByName || "").trim() || "—"}
                  >
                    {getInitials(String(taskDetailOpen.CreatedByName || "").trim() || "—")}
                  </div>
                  <span className="text-truncate">
                    {String(taskDetailOpen.CreatedByName || "").trim() || "—"}
                  </span>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="capa-detail-summary__activity-field-caption mb-1">
                  CREATED AT
                </div>
                <div className="text-truncate" style={{ fontSize: "0.68rem", fontWeight: 600 }}>
                  {taskDetailOpen.CreatedAt
                    ? utils.formatDateTimeDisplay(taskDetailOpen.CreatedAt)
                    : "—"}
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  if (!taskDetailOpen?.CapaId) return;
                  navigate(`/capa/${taskDetailOpen.CapaId}`);
                }}
              >
                Open CAPA
              </button>
              {canQuickDecide ? (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={actingTaskKey === String(taskDetailOpen.TaskKey || "")}
                    onClick={() => handleQuickDecision("Rejected")}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    disabled={actingTaskKey === String(taskDetailOpen.TaskKey || "")}
                    onClick={() => handleQuickDecision("Accepted")}
                  >
                    Accept
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </Sidemodal>
      ) : null}
      <DataTable
        data={filteredRows}
        columns={columns}
        actions={actions}
        actionsColumnLabel="Action"
        sortable={["DueDate", "Type", "TaskStatus", "OwnerName"]}
        showToggle={true}
      />
    </div>
  );
};

export default CapaTasks;
